import {
  DocumentIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

import {
  DemandExample,
  DemandExampleListResponse,
} from '../../services/types/demand-example-types';
import { Badge } from '../ui/catalyst/badge';
import { Button } from '../ui/catalyst/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/catalyst/table';
import { Text } from '../ui/catalyst/text';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { DemandExampleProcessingStatus } from './DemandExampleProcessingStatus';

interface DemandExamplesTableProps {
  data: DemandExampleListResponse | undefined;
  isLoading: boolean;
  error: any;
  currentPage: number;
  totalPages: number;
  onEdit: (example: DemandExample) => void;
  onDelete: (example: DemandExample) => void;
  onPageChange: (page: number) => void;
  onFilePreview: (example: DemandExample) => void;
  onManageTemplates: (example: DemandExample) => void;
}

export const DemandExamplesTable = ({
  data,
  isLoading,
  error,
  currentPage,
  totalPages,
  onEdit,
  onDelete,
  onPageChange,
  onFilePreview,
  onManageTemplates,
}: DemandExamplesTableProps) => {
  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-zinc-200">
        <div className="p-6 text-center">
          <Text>Loading demand examples...</Text>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-zinc-200">
        <div className="p-6 text-center">
          <Text className="text-red-600">Error loading demand examples</Text>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-zinc-200">
      <Table>
        <TableHead>
          <TableRow>
            <TableHeader>
              <div className="ml-3">Title</div>
            </TableHeader>
            <TableHeader>Matter Type</TableHeader>
            <TableHeader>Firm</TableHeader>
            <TableHeader>Tags</TableHeader>
            <TableHeader>File</TableHeader>
            <TableHeader>Status</TableHeader>
            <TableHeader>Created</TableHeader>
            <TableHeader>Actions</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {data?.results.map((example) => (
            <TableRow
              key={example.id}
              className="cursor-pointer hover:bg-zinc-50"
              onClick={() => onManageTemplates(example)}
            >
              <TableCell>
                <div>
                  {example.description ? (
                    <Tooltip>
                      <TooltipTrigger>
                        <span className="font-medium ml-3 cursor-help text-zinc-900">
                          {example.title || 'Untitled'}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        {example.description}
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <Text className="font-medium ml-3">
                      {example.title || 'Untitled'}
                    </Text>
                  )}
                </div>
              </TableCell>
              <TableCell>
                {example.matter_type && (
                  <Badge color="blue">{example.matter_type}</Badge>
                )}
              </TableCell>
              <TableCell>
                <Text>{example.firm_details?.name || 'No Firm'}</Text>
              </TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  {example.tags?.slice(0, 3).map((tag, index) => (
                    <Badge key={index} color="zinc" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                  {(example.tags?.length || 0) > 3 && (
                    <Tooltip>
                      <TooltipTrigger>
                        <span className="inline-flex items-center rounded-md bg-zinc-50 px-2 py-1 text-xs font-medium text-zinc-600 ring-1 ring-inset ring-zinc-500/10 cursor-help">
                          +{(example.tags?.length || 0) - 3}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <div className="space-y-1">
                          <div className="text-xs font-medium">
                            Additional tags:
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {example.tags?.slice(3).map((tag, index) => (
                              <span
                                key={index}
                                className="text-xs px-1.5 py-0.5 rounded"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
              </TableCell>
              <TableCell>
                {example.demand ? (
                  <button
                    type="button"
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.stopPropagation();
                      onFilePreview(example);
                    }}
                    className="cursor-pointer"
                  >
                    <Badge
                      color="green"
                      className="flex items-center gap-1 hover:bg-green-600 hover:text-white transition-colors"
                    >
                      <DocumentIcon className="h-3 w-3" />
                      File
                    </Badge>
                  </button>
                ) : (
                  <Text className="text-zinc-400">No file</Text>
                )}
              </TableCell>
              <TableCell>
                <DemandExampleProcessingStatus example={example} />
              </TableCell>
              <TableCell>
                <Text className="text-sm">
                  {new Date(example.created_at).toLocaleDateString()}
                </Text>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Button
                    plain
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.stopPropagation();
                      onEdit(example);
                    }}
                  >
                    <PencilIcon className="h-3 w-3" />
                  </Button>
                  <Button
                    plain
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.stopPropagation();
                      onDelete(example);
                    }}
                  >
                    <TrashIcon className="h-3 w-3 text-red-500" />
                  </Button>
                  <Button
                    plain
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.stopPropagation();
                      onManageTemplates(example);
                    }}
                  >
                    <span className="text-xs">Templates</span>
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="p-4 border-t border-zinc-200">
          <div className="flex justify-between items-center">
            <Button
              plain
              disabled={currentPage === 1}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                onPageChange(currentPage - 1)
              }
            >
              Previous
            </Button>
            <span className="text-sm text-zinc-600">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              plain
              disabled={currentPage === totalPages}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                onPageChange(currentPage + 1)
              }
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
